import { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { Button } from '@/components/ui/button';
import { Check, X, Pencil } from 'lucide-react';

type Props = {
  blockKey: string;
  initialContent: Record<string, unknown> | null;
  fallbackHtml: string;
  onSave: (blockKey: string, content: Record<string, unknown>) => Promise<void>;
  className?: string;
};

export function EditableText({
  blockKey,
  initialContent,
  fallbackHtml,
  onSave,
  className,
}: Props) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const originalJson = useRef<Record<string, unknown> | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder: 'Enter text…' }),
    ],
    editable: editing,
    content: initialContent ?? fallbackHtml,
  });

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editing);
    if (editing) {
      originalJson.current = editor.getJSON() as Record<string, unknown>;
      editor.commands.focus('end');
    }
  }, [editing, editor]);

  useEffect(() => {
    if (!editor || editing) return;
    editor.commands.setContent(initialContent ?? fallbackHtml, { emitUpdate: false });
  }, [initialContent]); // eslint-disable-line react-hooks/exhaustive-deps

  async function handleSave() {
    if (!editor) return;
    setSaving(true);
    try {
      await onSave(blockKey, editor.getJSON() as Record<string, unknown>);
      setEditing(false);
    } catch (err) {
      console.error('[EditableText] save failed:', err);
      toast.error('Failed to save — check the console for details.');
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    if (!editor) return;
    if (originalJson.current) {
      editor.commands.setContent(originalJson.current, { emitUpdate: false });
    }
    setEditing(false);
  }

  return (
    <span className={cn('group relative inline-block w-full', className)}>
      {/* Pencil is always in the DOM, shown via CSS group-hover — never disappears
          mid-cursor-travel because no JS state change is involved. */}
      {!editing && (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className="absolute right-0 top-0 z-50 flex h-6 w-6 items-center justify-center rounded-full bg-gold text-navy opacity-0 shadow-md transition-opacity group-hover:opacity-100"
          aria-label="Edit this text"
        >
          <Pencil className="h-3 w-3" />
        </button>
      )}

      <EditorContent
        editor={editor}
        className={cn(
          'outline-none',
          editing && 'rounded-sm px-1 ring-2 ring-gold/60 ring-offset-2',
        )}
      />

      {editing && (
        <span className="absolute -bottom-10 right-0 z-50 flex items-center gap-1 rounded-lg border border-border/70 bg-white px-2 py-1 shadow-lg">
          <Button
            size="sm"
            variant="gold"
            onClick={handleSave}
            disabled={saving}
            className="h-7 px-2 text-xs"
          >
            <Check className="mr-1 h-3 w-3" />
            {saving ? 'Saving…' : 'Save'}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleCancel}
            disabled={saving}
            className="h-7 px-2 text-xs"
          >
            <X className="mr-1 h-3 w-3" />
            Cancel
          </Button>
        </span>
      )}
    </span>
  );
}
